import parse from 'html-react-parser';

import { exerciseTypes } from 'z-js-commons';

import { exerciseRandomSeed, shuffle } from './shuffle';
import { orderOptions } from './exercise';
import { replaceWithAbsolutePath, replaceAttachmentsPath } from './media-path-replacer';
import { S3_GEOGEBRA_URL } from '../config';
import { getAnswerContent, getAnswerType } from './checker';
import { decryptExerciseList } from './crypto';

export const decorateExercise = async (exercise, randomSeed, isSolution = false) => {
    const exerciseType = exerciseTypes.getExerciseTypeFromName(exercise.typeLabel);

    if (!exercise.json) {
        return exercise;
    }

    const isShufflable = !!exercise.json.shuffle && !isSolution;

    if (exercise.json.list) {
        // If an answer list is encrypted, then it's a string, not an array: I must decrypt it.
        // Otherwise I leave it untouched.
        if (!Array.isArray(exercise.json.list)) {
            try {
                exercise.json.list = await decryptExerciseList(exercise.id.toString(), exercise.json.list);
            } catch (e) {
                console.error(e);
                return null;
            }
        }
    }

    if (exercise.json.prompt) {
        exercise.prompt = exercise.json.prompt;
    }
    exercise = decorateAttachments(exercise);

    exercise.feedbacks = decorateFeedbacks(exercise.json.feedbacks);
    exercise.treatment = decorateTreatment(exercise.json.treatment);

    switch (exerciseType) {
        case exerciseTypes.TRUE_FALSE:
            return decorateTrueFalse(exercise, isShufflable, randomSeed);
        case exerciseTypes.MULTIPLE_CHOICE:
            return decorateMultipleChoice(exercise, isShufflable, randomSeed);
        case exerciseTypes.DRAG_DROP:
            return decorateDragAndDrop(exercise, isShufflable, randomSeed);
        case exerciseTypes.FILL_BLANKS:
            return decorateFillBlanks(exercise);
        case exerciseTypes.FILL_CHOICE:
            return decorateFillChoice(exercise, isShufflable, randomSeed);
        case exerciseTypes.SHOTS:
            return decorateBarrage(exercise);
        case exerciseTypes.OPEN_ANSWER:
            return decorateOpenAnswer(exercise);
        case exerciseTypes.FIND_ERROR:
            return decorateFindError(exercise);
        case exerciseTypes.CHECKER:
            return decorateChecker(exercise);
        default:
            return exercise;
    }
};

export const decorateDragAndDrop = (exercise, isShufflable, randomSeed) => {
    if (isShufflable) {
        const random = exerciseRandomSeed(randomSeed, exercise.id);
        exercise.json.list = shuffle(exercise.json.list, random);
    }
    const question = replaceWithAbsolutePath(exercise.json.question);
    const options =
        exercise &&
        exercise.json.list.map((option) => {
            const values =
                option &&
                option.values.map((value) => {
                    return {
                        ...value,
                        text: replaceWithAbsolutePath(value.text),
                    };
                });
            return {
                ...option,
                values,
            };
        });

    delete exercise.json;

    return {
        ...exercise,
        question,
        options,
    };
};

export const decorateMultipleChoice = (exercise, isShufflable, randomSeed) => {
    if (isShufflable) {
        const random = exerciseRandomSeed(randomSeed, exercise.id);
        exercise.json.list = shuffle(exercise.json.list, random);
    }
    const question = replaceWithAbsolutePath(exercise.json.question);
    const shuffledOptions = exercise.json.list;

    const options = shuffledOptions.map((option) => {
        return {
            ...option,
            text: replaceWithAbsolutePath(option.text),
        };
    });

    delete exercise.json;

    return {
        ...exercise,
        question,
        options,
    };
};

export const decorateTrueFalse = (exercise, isShufflable, randomSeed) => {
    if (isShufflable) {
        const random = exerciseRandomSeed(randomSeed, exercise.id);
        exercise.json.list = shuffle(exercise.json.list, random);
    }
    const shuffled = exercise.json.list;
    const question = replaceWithAbsolutePath(exercise.json.question);
    delete exercise.json;

    return {
        ...exercise,
        question,
        questions: shuffled.map((question) => {
            question.question = replaceWithAbsolutePath(question.question);
            return {
                ...question,
                options: [
                    {
                        id: true,
                        text: question.trueString,
                        correct: question.correct === true,
                    },
                    {
                        id: false,
                        text: question.falseString,
                        correct: question.correct === false,
                    },
                ],
            };
        }),
    };
};

export const decorateFillBlanks = (exercise) => {
    const question = replaceWithAbsolutePath(exercise.json.question);

    const options = orderOptions(question, exercise.json.list).map((item) => {
        return {
            id: item.id,
            values: item.values.map((item) => item.text),
            feedback: item.feedback,
        };
    });

    delete exercise.json;

    return {
        ...exercise,
        question,
        options,
    };
};

export const decorateFillChoice = (exercise, isShufflable, randomSeed) => {
    const question = replaceWithAbsolutePath(exercise.json.question);
    if (isShufflable) {
        exercise.json.list = exercise.json.list.map((option) => {
            const random = exerciseRandomSeed(randomSeed, exercise.id * option.id);
            option.values = shuffle(option.values, random);
            return option;
        });
    }
    const options = orderOptions(question, exercise.json.list).map((option) => ({
        ...option,
        values: option.values.map((value) => ({ ...value, type: 'listed' })),
    }));
    const useSelect = exercise?.json?.useSelect;

    delete exercise.json;
    return {
        ...exercise,
        question,
        options,
        useSelect,
    };
};

export const decorateBarrage = (exercise) => {
    const question = replaceWithAbsolutePath(exercise.json.question);
    const shots = exercise.json.shots;
    const options = exercise.json.list;

    delete exercise.json;

    return {
        ...exercise,
        shots: shots.map((shot) => {
            return {
                ...shot,
                id: `${shot.id}`,
                idRef: `${shot.idRef}`,
                text: replaceWithAbsolutePath(shot.text),
            };
        }),
        question,
        options: options.map((option) => {
            return { ...option, id: `${option.id}`, text: replaceWithAbsolutePath(option.text) };
        }),
    };
};

export const decorateOpenAnswer = (exercise) => {
    const question = replaceWithAbsolutePath(exercise.json.question);
    const correctAnswer = exercise.json.correctAnswer;
    delete exercise.json;

    return { ...exercise, question, correctAnswer };
};

export const decorateFindError = (exercise) => {
    const question = replaceWithAbsolutePath(exercise.json.question);
    const options = exercise.json.list;

    delete exercise.json;

    let wrongWords = [];
    let correctWords = [];
    let nodeStartIndexWrong = 0;
    let nodeStartIndexCorrect = 0;

    parse(question.replace(/<error_ref (.*?)\/>/g, '<error_ref $1></error_ref>'), {
        replace: ({ type, data, name, attribs }) => {
            if (type === 'tag' && name === 'error_ref') {
                // Sostituisco la stringa con la parola corretta/sbagliata e prendo gli indici
                const id = attribs.idref.replace('_', '');
                const option = options.find((el) => {
                    return el.id === id;
                });
                const wrongAnswer = option.values.find((el) => {
                    return !el.correct;
                });

                wrongAnswer.text = wrongAnswer.text.trim();

                const correctAnswer = option.values.find((el) => {
                    return el.correct;
                });

                wrongWords.push({
                    id: attribs.idref,
                    start: nodeStartIndexWrong,
                    end: nodeStartIndexWrong + wrongAnswer.text.length - 1,
                    text: wrongAnswer.text,
                });

                correctWords.push({
                    id: attribs.idref,
                    start: nodeStartIndexCorrect,
                    end: nodeStartIndexCorrect + correctAnswer.text.length - 1,
                    text: correctAnswer.text,
                });

                nodeStartIndexWrong += wrongAnswer.text.length;
                nodeStartIndexCorrect += correctAnswer.text.length;

                return;
            }

            if (type === 'text') {
                nodeStartIndexWrong += data.length;
                nodeStartIndexCorrect += data.length;
            }
        },
    });

    // Sostituisco gli error_ref con le parole errate
    const plainQuestionWrongArray = question.split(/<error_ref (.*?)\/>/g).map((item) => {
        if (!item.startsWith('idref')) {
            return item;
        }

        const id = item.substring(item.lastIndexOf('_') + 1, item.lastIndexOf('"'));
        return wrongWords.find((word) => word.id === `_${id}`).text;
    });

    // Sostituisco gli error_ref con le parole esatte
    const plainQuestionCorrectArray = question.split(/<error_ref (.*?)\/>/g).map((item) => {
        if (!item.startsWith('idref')) {
            return item;
        }

        const id = item.substring(item.lastIndexOf('_') + 1, item.lastIndexOf('"'));
        return correctWords.find((word) => word.id === `_${id}`).text;
    });

    const plainQuestionWrong = plainQuestionWrongArray.join('');
    const plainQuestionCorrect = plainQuestionCorrectArray.join('');

    return {
        ...exercise,
        question,
        options,
        correctWords,
        wrongWords,
        plainQuestionWrong,
        plainQuestionCorrect,
    };
};

export const decorateAttachments = (exercise) => {
    const attachments = exercise.json.attachments ? exercise.json.attachments.list : [];
    const audioAttachment = attachments.find((attachment) => attachment.type === 'audio');
    const videoAttachment = attachments.find((attachment) => attachment.type === 'embeddedVideo' && attachment.source === 'Youtube');

    const imagesAttachment = attachments
        .filter((attachment) => attachment.type === 'image')
        .map((item) => ({ ...item, src: replaceAttachmentsPath(item.src) }));

    const geogebraAttachment = getGeogebraInfo(exercise.geogebraXml);

    return {
        ...exercise,
        attachments: {
            audio: audioAttachment && {
                ...audioAttachment,
                src: replaceAttachmentsPath(audioAttachment.src),
            },
            video: videoAttachment && { ...videoAttachment },
            images: imagesAttachment,
            geogebra: geogebraAttachment,
        },
    };
};

export const decorateFeedbacks = (feedbacks) => {
    if (!feedbacks) return feedbacks;
    return {
        genericFeedback: replaceWithAbsolutePath(feedbacks.genericFeedback),
        genericErrorFeedback: replaceWithAbsolutePath(feedbacks.genericErrorFeedback),
        genericCorrectFeedback: replaceWithAbsolutePath(feedbacks.genericCorrectFeedback),
    };
};

export const decorateTreatment = (treatment) => {
    if (!treatment || !treatment.steps || treatment.steps.length === 0) {
        return [];
    }

    return treatment.steps.map((step) => {
        return {
            text: replaceWithAbsolutePath(step),
        };
    });
};

export const getGeogebraInfo = (geogebraXml) => {
    if (!geogebraXml) return null;

    let src, previewUrl;

    parse(geogebraXml, {
        replace: ({ type, data, name, attribs }) => {
            if (type === 'tag' && name === 'ggb') {
                src = attribs.src;
            }
            if (type === 'tag' && name === 'preview') {
                previewUrl = attribs.src;
            }
            return data;
        },
    });

    return { src: `${S3_GEOGEBRA_URL}/${src}`, previewUrl };
};

export const decorateChecker = (exercise) => {
    const question = replaceWithAbsolutePath(exercise.json.question);

    const id = exercise.id;
    const typeLabel = exercise.typeLabel;
    const prompt = exercise.json.prompt;
    const assertion = exercise.json.assertion;
    const existenceConditions = exercise.json.list.map((cde) => cde.name);

    const answerType = getAnswerType(exercise);
    const answerContent = getAnswerContent(exercise);

    delete exercise.json;

    return {
        ...exercise,
        id,
        typeLabel,
        prompt,
        question,
        assertion,
        answerType,
        answerContent,
        existenceConditions,
    };
};
